import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Mail, BarChart3, X, Eye, TrendingUp, TrendingDown, Users } from 'lucide-react';
import { employeeAPI, formatCurrency } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

// Granular permissions admins can grant per employee.
const PERMISSION_CATALOG = [
  { key: 'invoice:create', label: 'Create Tax Invoices' },
  { key: 'invoice:edit', label: 'Edit Tax Invoices' },
  { key: 'invoice:delete', label: 'Delete Tax Invoices' },
  { key: 'receipt:create', label: 'Create LR / Receipts' },
  { key: 'receipt:edit', label: 'Edit LR / Receipts' },
  { key: 'vehicle:create', label: 'Create/Edit Vehicles' },
  { key: 'driver:create', label: 'Create/Edit Drivers' },
  { key: 'memo:create', label: 'Create Collection Memos' },
  { key: 'journal:create', label: 'Post Journal Entries' },
  { key: 'ledger:view', label: 'View Ledger Reports' },
  { key: 'reports:export', label: 'Export PDFs / Excel' },
  { key: 'parties:manage', label: 'Manage Parties' },
  { key: 'transactions:view', label: 'View Transactions' },
  { key: 'journal:view', label: 'View Journal Entries' },
  { key: 'trial_balance:view', label: 'View Trial Balance' },
  { key: 'profit_loss:view', label: 'View Profit & Loss' },
  { key: 'balance_sheet:view', label: 'View Balance Sheet' },
  { key: 'advances:manage', label: 'Manage Advances' },
  { key: 'dashboard:view', label: 'View Dashboard' },
  { key: 'gst:view', label: 'View GST Reports' },
  { key: 'tds:view', label: 'View TDS Reports' },
];

const ROLE_OPTIONS = [
  { v: 'employee', l: 'Employee (default)' },
  { v: 'manager', l: 'Manager' },
  { v: 'accountant', l: 'Accountant' },
  { v: 'admin', l: 'Admin (full access)' },
];

export default function EmployeeManagement() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statsModal, setStatsModal] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', username: '', password: '', role: 'employee',
    position: '', permissions: [], view_history: false, branch_ids: [],
  });
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    api.get('/branches').then(r => setBranches((r.data?.branches || []).filter(b => b.is_active))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await employeeAPI.list();
      setEmployees(r.data || []);
    } catch { toast.error('Failed to load employees'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', username: '', password: '', role: 'employee', position: '', permissions: [], view_history: false, branch_ids: [] });
    setOpen(true);
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name || '', email: e.email || '', username: e.username || '',
      password: '', role: e.role || 'employee', position: e.position || '',
      permissions: e.permissions || [], view_history: !!e.view_history,
      branch_ids: e.branch_ids || [],
    });
    setOpen(true);
  };

  const toggleBranch = (id) => {
    setForm(f => ({
      ...f,
      branch_ids: f.branch_ids.includes(id) ? f.branch_ids.filter(b => b !== id) : [...f.branch_ids, id],
    }));
  };

  const togglePerm = (k) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(k)
        ? f.permissions.filter(p => p !== k)
        : [...f.permissions, k],
    }));
  };

  const save = async () => {
    if (!form.name || !form.email || !form.username) return toast.error('Name, email and username are required');
    if (!editing && !form.password) return toast.error('Password is required for new employee');
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await employeeAPI.update(editing.user_id, payload);
        toast.success('Employee updated');
      } else {
        await employeeAPI.create(form);
        toast.success('Employee created — credentials emailed');
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete employee ${e.name}?`)) return;
    try { await employeeAPI.delete(e.user_id); toast.success('Deleted'); load(); }
    catch (er) { toast.error(er.response?.data?.detail || 'Failed'); }
  };

  const showStats = async (e) => {
    setStatsModal(e); setStatsData(null);
    try {
      const r = await employeeAPI.stats(e.user_id);
      setStatsData(r.data);
    } catch { toast.error('Failed to load stats'); setStatsModal(null); }
  };

  if (!isAdmin) return <div className="p-6 text-center text-slate-500">Admin access only.</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 dark:text-slate-100">Employees</h1>
          <p className="text-sm text-slate-500 mt-1">Manage user accounts, permissions, and access scope.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAnalytics(v => !v)} className="btn-secondary flex items-center gap-2 text-sm" data-testid="toggle-analytics-btn">
            <TrendingUp className="w-4 h-4" /> {showAnalytics ? 'Hide Analytics' : 'Analytics Dashboard'}
          </button>
          <button onClick={openCreate} className="btn-accent flex items-center gap-2" data-testid="add-employee-btn">
            <Plus className="w-4 h-4" /> Add Employee
          </button>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && <EmployeeAnalyticsDashboard />}

      {loading ? <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div> : (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full data-table" data-testid="employees-table">
              <thead><tr><th>Name</th><th>Position</th><th>Username / Email</th><th>Role</th><th className="text-center">Sees History</th><th className="text-center">Actions</th></tr></thead>
              <tbody>
                {employees.map(e => (
                  <tr key={e.user_id}>
                    <td className="font-medium">{e.name}</td>
                    <td className="text-sm text-slate-500">{e.position || '-'}</td>
                    <td className="text-sm">
                      <div>{e.username || '-'}</div>
                      <div className="text-slate-400 text-xs">{e.email}</div>
                    </td>
                    <td><span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 capitalize">{e.role}</span></td>
                    <td>
                      {(e.branch_ids || []).length === 0
                        ? <span className="text-[10px] text-slate-400">All (unrestricted)</span>
                        : <div className="flex flex-wrap gap-1">{(e.branch_ids || []).map(bid => { const br = branches.find(b => b.branch_id === bid); return <span key={bid} className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">{br ? br.code : bid}</span>; })}</div>}
                    </td>
                    <td className="text-center"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${e.view_history ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{e.view_history ? 'YES' : 'NO'}</span></td>
                    <td>
                      <div className="flex justify-center gap-1">
                        <button onClick={() => showStats(e)} className="p-1.5 hover:bg-slate-100 rounded" title="View activity stats" data-testid={`stats-${e.user_id}`}><BarChart3 className="w-3.5 h-3.5 text-blue-600" /></button>
                        <button onClick={() => openEdit(e)} className="p-1.5 hover:bg-slate-100 rounded" title="Edit" data-testid={`edit-employee-${e.user_id}`}><Edit className="w-3.5 h-3.5 text-slate-500" /></button>
                        <button onClick={() => remove(e)} className="p-1.5 hover:bg-rose-50 rounded" title="Delete"><Trash2 className="w-3.5 h-3.5 text-rose-500" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {employees.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-12">No employees yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Employee' : 'Add Employee'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="form-label">Name</label><input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="emp-name" /></div>
            <div><label className="form-label">Position / Tag</label><input className="form-input" placeholder="e.g. Senior Accountant" value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} data-testid="emp-position" /></div>
            <div><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="emp-email" /></div>
            <div><label className="form-label">Username</label><input className="form-input" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} data-testid="emp-username" /></div>
            <div><label className="form-label">{editing ? 'New Password (leave blank to keep)' : 'Password'}</label><input className="form-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="emp-password" /></div>
            <div><label className="form-label">Role</label>
              <select className="form-input" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} data-testid="emp-role">
                {ROLE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
          </div>

          {/* Permissions */}
          <div className="mt-4">
            <label className="form-label mb-2 block">Permissions ({form.permissions.length} selected)</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border border-slate-200 dark:border-slate-700 rounded-md p-3 max-h-[200px] overflow-auto">
              {PERMISSION_CATALOG.map(p => (
                <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 px-2 py-1 rounded">
                  <input
                    type="checkbox"
                    checked={form.permissions.includes(p.key)}
                    onChange={() => togglePerm(p.key)}
                    data-testid={`perm-${p.key}`}
                    className="rounded text-purple-600 focus:ring-purple-500"
                  />
                  <span>{p.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Assigned Branches */}
          <div className="mt-4">
            <label className="form-label mb-2 block">Assigned Branches ({form.branch_ids.length} selected)</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border border-slate-200 dark:border-slate-700 rounded-md p-3 max-h-[140px] overflow-auto">
              {branches.map(b => (
                <label key={b.branch_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 px-2 py-1 rounded">
                  <input type="checkbox" checked={form.branch_ids.includes(b.branch_id)} onChange={() => toggleBranch(b.branch_id)}
                    data-testid={`branch-assign-${b.branch_id}`} className="rounded text-purple-600 focus:ring-purple-500" />
                  <span>{b.name} <span className="text-[10px] text-slate-400 font-mono">({b.code})</span></span>
                </label>
              ))}
              {branches.length === 0 && <p className="text-xs text-slate-400 col-span-2">No branches created yet.</p>}
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Leave empty for unrestricted access to all branches. When assigned, employee sees only these branches' LRs, invoices, parties and reports.</p>
          </div>

          {/* View history toggle */}
          <div className="mt-4 flex items-start justify-between gap-3 border border-slate-200 dark:border-slate-700 rounded-md p-3">
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Show company data created before this employee joined</p>
              <p className="text-xs text-slate-500 mt-0.5">When ON, employee can VIEW old records (read-only). When OFF, they only see records created on or after their start date.</p>
            </div>
            <button
              type="button"
              onClick={() => setForm({ ...form, view_history: !form.view_history })}
              data-testid="emp-view-history-toggle"
              aria-pressed={form.view_history}
              className={`shrink-0 mt-1 inline-flex items-center h-6 w-11 rounded-full transition-colors ${form.view_history ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
            >
              <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${form.view_history ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="emp-save">{editing ? 'Save Changes' : 'Create Employee'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats modal */}
      {statsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-3">
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-lg w-full">
            <div className="border-b border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 dark:text-slate-100">Activity — {statsModal.name}</h3>
              <button onClick={() => setStatsModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              {!statsData ? <p className="text-sm text-slate-500">Loading…</p> : (
                <>
                  <p className="text-xs text-slate-500">{statsModal.position || statsModal.role} · joined {statsModal.joined_at?.slice(0, 10) || '—'}</p>
                  <div className="grid grid-cols-2 gap-3" data-testid="emp-stats-grid">
                    {[
                      ['LR / Receipts', statsData.counts.receipts, 'bg-blue-50 text-blue-700'],
                      ['Tax Invoices', statsData.counts.tax_invoices, 'bg-emerald-50 text-emerald-700'],
                      ['Sales Invoices', statsData.counts.sales_invoices, 'bg-emerald-50 text-emerald-700'],
                      ['Purchase Invoices', statsData.counts.purchase_invoices, 'bg-amber-50 text-amber-700'],
                      ['Collection Memos', statsData.counts.collection_memos, 'bg-purple-50 text-purple-700'],
                      ['Journal Entries', statsData.counts.journal_entries, 'bg-indigo-50 text-indigo-700'],
                      ['Advances', statsData.counts.advances, 'bg-pink-50 text-pink-700'],
                    ].map(([label, val, color]) => (
                      <div key={label} className={`rounded-lg p-3 ${color}`}>
                        <div className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{label}</div>
                        <div className="text-2xl font-bold mt-1">{val.toLocaleString('en-IN')}</div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-3 mt-3">
                    <div className="text-xs text-slate-500">Revenue generated by sales invoices</div>
                    <div className="text-lg font-mono font-bold text-slate-900 dark:text-slate-100">Rs. {statsData.revenue_generated.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PERIOD_OPTIONS = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'half_year', label: '6 Months' },
  { value: 'year', label: 'Year' },
];

function EmployeeAnalyticsDashboard() {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    employeeAPI.analytics({ period }).then(r => setData(r.data)).catch(() => toast.error('Failed to load analytics')).finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const { employees, summary } = data;

  return (
    <div className="space-y-4" data-testid="employee-analytics-dashboard">
      {/* Period selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading font-semibold text-lg text-slate-800 dark:text-slate-200">Performance Analytics</h2>
        <div className="flex gap-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-1" data-testid="analytics-period-selector">
          {PERIOD_OPTIONS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              data-testid={`period-${p.value}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${period === p.value ? 'bg-purple-600 text-white shadow-sm' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-purple-500" />
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Employees</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="analytics-total-employees">{summary.total_employees}</div>
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-emerald-500" />
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Sales</span>
          </div>
          <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-400" data-testid="analytics-total-sales">{summary.total_sales}</div>
          <div className="text-xs text-slate-500 mt-0.5">Rs. {formatCurrency(summary.total_sales_amount)}</div>
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="w-4 h-4 text-amber-500" />
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Purchases</span>
          </div>
          <div className="text-2xl font-bold text-amber-700 dark:text-amber-400" data-testid="analytics-total-purchases">{summary.total_purchases}</div>
          <div className="text-xs text-slate-500 mt-0.5">Rs. {formatCurrency(summary.total_purchase_amount)}</div>
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-4 h-4 text-blue-500" />
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Total Volume</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{summary.total_sales + summary.total_purchases}</div>
          <div className="text-xs text-slate-500 mt-0.5">Rs. {formatCurrency(summary.total_sales_amount + summary.total_purchase_amount)}</div>
        </div>
      </div>

      {/* Employee breakdown table */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full data-table" data-testid="analytics-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Position</th>
                <th className="text-center">Sales</th>
                <th className="text-right">Sales Amount</th>
                <th className="text-center">Purchases</th>
                <th className="text-right">Purchase Amount</th>
                <th className="text-center">Receipts</th>
                <th className="text-center">Journal Entries</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(e => (
                <tr key={e.user_id} data-testid={`analytics-row-${e.user_id}`}>
                  <td className="font-medium text-sm">{e.name}</td>
                  <td className="text-xs text-slate-500">{e.position || e.role}</td>
                  <td className="text-center">
                    <span className={`inline-block min-w-[28px] text-center text-xs font-bold px-2 py-0.5 rounded-full ${e.sales_count > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-400'}`}>{e.sales_count}</span>
                  </td>
                  <td className="text-right font-mono text-sm">{e.sales_amount > 0 ? `Rs. ${formatCurrency(e.sales_amount)}` : '-'}</td>
                  <td className="text-center">
                    <span className={`inline-block min-w-[28px] text-center text-xs font-bold px-2 py-0.5 rounded-full ${e.purchase_count > 0 ? 'bg-amber-50 text-amber-700' : 'bg-slate-50 text-slate-400'}`}>{e.purchase_count}</span>
                  </td>
                  <td className="text-right font-mono text-sm">{e.purchase_amount > 0 ? `Rs. ${formatCurrency(e.purchase_amount)}` : '-'}</td>
                  <td className="text-center text-sm">{e.receipts_count || '-'}</td>
                  <td className="text-center text-sm">{e.journal_entries_count || '-'}</td>
                </tr>
              ))}
              {employees.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">No activity data for this period</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
